// Date-priority tiers for ordering files inside a category before combining.
// Shared by the server (settings registry/validation, order resolver) and the
// client (Settings › File Ordering page) so the tier ids never drift.
//
// Each tier names ONE date source on a file:
//   filename — a date parsed from the uploaded filename (e.g. 2024-03-15_report.pdf)
//   document — a date printed inside the document (first pages of the converted PDF)
//   metadata — the file's own metadata date (PDF CreationDate/ModDate, email Date header)
// The configured priority is the order in which those sources are consulted; the
// first one carrying a date wins. Files with no date in any source fall back to the
// upload date (createdAt). The default is filename → document → metadata.

export const FILE_ORDERING_TIER = Object.freeze({
  FILENAME: 'filename',
  DOCUMENT: 'document',
  METADATA: 'metadata'
})

export const FILE_ORDERING_TIERS = Object.freeze([FILE_ORDERING_TIER.FILENAME, FILE_ORDERING_TIER.DOCUMENT, FILE_ORDERING_TIER.METADATA])

export const DEFAULT_FILE_ORDERING_PRIORITY = Object.freeze([...FILE_ORDERING_TIERS])

export const FILE_ORDERING_SETTING_KEY = 'fileOrdering'

// Example filenames shown next to the "Date in filename" tier on the Settings page. Kept
// here so the server's parser test can assert that every example really parses to a date
// (the copy can never promise a format the parser does not support).
export const FILE_ORDERING_FILENAME_EXAMPLES = Object.freeze(['2024-03-15_report.pdf', 'IMG_20230505.jpg'])

/**
 * A priority is valid only when it is a permutation of exactly the known tiers:
 * every tier present once, nothing missing, nothing unknown, no duplicates.
 * Returns `null` when valid, otherwise a human-readable reason.
 */
export function getFileOrderingPriorityError(priority) {
  if (!Array.isArray(priority)) return 'priority must be an array of tiers'
  if (priority.length !== FILE_ORDERING_TIERS.length) {
    return `priority must contain exactly ${FILE_ORDERING_TIERS.length} tiers (${FILE_ORDERING_TIERS.join(', ')})`
  }
  const unknown = priority.filter(tier => !FILE_ORDERING_TIERS.includes(tier))
  if (unknown.length) return `unknown tier(s): ${unknown.map(String).join(', ')}`
  const duplicates = priority.filter((tier, index) => priority.indexOf(tier) !== index)
  if (duplicates.length) return `duplicate tier(s): ${duplicates.join(', ')}`
  const missing = FILE_ORDERING_TIERS.filter(tier => !priority.includes(tier))
  if (missing.length) return `missing tier(s): ${missing.join(', ')}`
  return null
}

export function isValidFileOrderingPriority(priority) {
  return getFileOrderingPriorityError(priority) === null
}
